import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import {
  LaunchpadHome, LaunchpadCreate, LaunchpadExplore, LaunchpadTokenPage,
} from "./launchpad/Launchpad";
import { TransparencySection } from "./launchpad/Transparency";
import "@/App.css";
import {
  ShoppingCart,
  LineChart,
  Send,
  Twitter,
  Youtube,
  Instagram,
  Coins,
  PieChart,
  Flame,
  Droplets,
  Shield,
  Eye,
  Users,
  Rocket,
  Compass,
  Gift,
  Trophy,
  Image as ImageIcon,
  Heart,
  ExternalLink,
  Copy,
  Check,
  Sparkles,
  Zap,
  TrendingUp,
  Download,
  Wallet,
  ArrowRightLeft,
  CircleDollarSign,
  BadgeCheck,
  Lock,
  Target,
  HandHeart,
  Menu,
  X as XIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Radio,
  Newspaper,
  Bot,
  Mail,
} from "lucide-react";

// Editable links — replace with real URLs once available
const LINKS = {
  buy: "https://pump.fun/coin/5fBkvLDPsuMQ8CymHBtahRrP7YZwHKp17SzqVa2wpump",
  chart: "https://dexscreener.com/solana/5fBkvLDPsuMQ8CymHBtahRrP7YZwHKp17SzqVa2wpump",
  telegram: "https://t.me/StickyTokenCommunity",
  twitter: "https://x.com/StickyEcosystem",
  youtube: "https://www.youtube.com/@StickyLikeYou",
  instagram: "https://instagram.com/stickylikeyou",
  unicef: "https://www.unicef.org/innovation/CryptoFund",
};

const CONTRACT = "5fBkvLDPsuMQ8CymHBtahRrP7YZwHKp17SzqVa2wpump";

// Meme Contest Round 2 — LIVE window: starts at deploy, ends 36h later
const CONTEST_R2_END_AT = new Date("2026-10-02T07:06:45Z").getTime();

// Team wallet address on Solana (3% allocation) — when set, "View Team Wallet" button activates
const TEAM_WALLET = "EtD6ZvPFns88g699nLhuFf9nG4PJntzru8tBU7rPKoMD";

// Whitepaper URL (PDF or DOCX) — when set, "Download Whitepaper" button activates
// Place file at /app/frontend/public/assets/<filename> and set this to "/assets/<filename>"
const WHITEPAPER_URL = "/assets/STICKY_Whitepaper.docx";

// DexScreener live data — pairAddress can be the Solana token mint address (Dex API
// resolves the pair automatically for launched Pump.fun / Raydium tokens).
const DEXSCREENER = {
  chain: "solana",
  pairAddress: "5fBkvLDPsuMQ8CymHBtahRrP7YZwHKp17SzqVa2wpump",
};

const ASSETS = {
  mascot: "/assets/sticky-mascot-v2.png?v=1",
  coin: "/assets/sticky-coin-tr.png",
  coinGold: "/assets/sticky-coin-gold.webp?v=2",
};

// Real Sticky App screenshots for carousel — add/replace paths here
const APP_SCREENSHOTS = [
  { src: "/assets/app-1.webp", label: "Sticky Login" },
  { src: "/assets/app-2.webp", label: "Sticky Profile" },
  { src: "/assets/app-radio-usa.webp", label: "Sticky Radio · USA" },
  { src: "/assets/app-radio-eu.webp", label: "Sticky Radio · Europe" },
  { src: "/assets/app-3.webp", label: "Crypto News" },
  { src: "/assets/app-news-web3.webp", label: "Crypto News · Web3 & AI" },
  { src: "/assets/app-news-eth.webp", label: "Crypto News · Ethereum" },
  { src: "/assets/app-news-meme.webp", label: "Crypto News · Meme" },
  { src: "/assets/app-4.webp", label: "News Categories" },
];

// Navigation items (id must match a section data-testid id; type "route" is a router link)
const NAV_ITEMS = [
  { id: "about", label: "About" },
  { id: "app", label: "Sticky App" },
  { id: "markets", label: "Markets" },
  { id: "tokenomics", label: "Tokenomics" },
  { id: "howtobuy", label: "How to Buy" },
  { id: "community", label: "Community" },
  { id: "roadmap", label: "Roadmap" },
  { id: "impact", label: "Impact" },
  { type: "route", to: "/launchpad", label: "Launchpad" },
  { id: "hiring", label: "We're Hiring" },
  { id: "rugcheck-wrap", label: "Transparency" },
];

function Button({ variant = "primary", icon: Icon, children, href, testId }) {
  const cls =
    variant === "primary"
      ? "sticky-btn sticky-btn-primary"
      : variant === "secondary"
      ? "sticky-btn sticky-btn-secondary"
      : "sticky-btn sticky-btn-ghost";
  const isHash = typeof href === "string" && href.startsWith("#");
  return (
    <a
      href={href}
      target={isHash ? undefined : "_blank"}
      rel={isHash ? undefined : "noopener noreferrer"}
      className={cls}
      data-testid={testId}
    >
      {Icon && <Icon size={18} strokeWidth={2.4} />}
      <span>{children}</span>
    </a>
  );
}

function Section({ id, eyebrow, title, accentWord, children }) {
  // accentWord: portion of title to color yellow
  const renderTitle = () => {
    if (!accentWord) return title;
    const parts = title.split(accentWord);
    return (
      <>
        {parts[0]}
        <span className="accent">{accentWord}</span>
        {parts[1]}
      </>
    );
  };
  return (
    <section id={id} className="sticky-section sticky-reveal" data-testid={`section-${id}`}>
      {eyebrow && <span className="sticky-eyebrow">{eyebrow}</span>}
      {title && <h2 className="sticky-h2">{renderTitle()}</h2>}
      {children}
    </section>
  );
}

function ContractRow() {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(CONTRACT).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className="sticky-contract"
      onClick={onCopy}
      role="button"
      tabIndex={0}
      data-testid="contract-row"
    >
      <span className="sticky-contract-label">CA</span>
      <span style={{ flex: 1, textAlign: "left" }}>{CONTRACT}</span>
      {copied ? (
        <Check size={16} color="#FFD84D" />
      ) : (
        <Copy size={16} color="#AAAAAA" />
      )}
    </div>
  );
}

function RugcheckButton() {
  // Treat any value containing "coming" / "launch" / placeholder as inactive
  const caActive =
    typeof CONTRACT === "string" &&
    CONTRACT.length > 20 &&
    !/coming|launch|soon/i.test(CONTRACT);
  const walletActive = typeof TEAM_WALLET === "string" && TEAM_WALLET.length > 20;
  const wpActive = typeof WHITEPAPER_URL === "string" && WHITEPAPER_URL.length > 0;

  const buttons = [
    {
      key: "rugcheck",
      active: caActive,
      label: "Verified on Rugcheck",
      activeSub: "Tap to view full security report",
      soonSub: "Coming Soon — activates at launch",
      href: caActive ? `https://rugcheck.xyz/tokens/${CONTRACT}` : undefined,
      Icon: Shield,
    },
    {
      key: "birdeye",
      active: caActive,
      label: "Track on Birdeye",
      activeSub: "Tap to view live market data",
      soonSub: "Coming Soon — auto-indexed after launch",
      href: caActive ? `https://birdeye.so/token/${CONTRACT}?chain=solana` : undefined,
      Icon: Eye,
    },
    {
      key: "whitepaper",
      active: wpActive,
      label: "Download Whitepaper",
      activeSub: "Tap to download the full STICKY whitepaper",
      soonSub: "Coming Soon — whitepaper releases shortly",
      href: wpActive ? WHITEPAPER_URL : undefined,
      Icon: Download,
      isDownload: true,
    },
  ];

  return (
    <div className="sticky-rugcheck-wrap" data-testid="rugcheck-wrap">
      {buttons.map(({ key, active, label, activeSub, soonSub, href, Icon: Ic, isDownload }) => (
        <a
          key={key}
          href={href}
          target={active && !isDownload ? "_blank" : undefined}
          rel={active && !isDownload ? "noopener noreferrer" : undefined}
          download={active && isDownload ? "" : undefined}
          className={`sticky-rugcheck-btn ${active ? "is-active" : "is-soon"}`}
          data-testid={`${key}-btn`}
          onClick={(e) => { if (!active) e.preventDefault(); }}
          aria-disabled={!active}
        >
          <span className="sticky-rugcheck-icon">
            <Ic size={16} strokeWidth={2.6} />
          </span>
          <span className="sticky-rugcheck-text">
            <strong>{label}</strong>
            <small>{active ? activeSub : soonSub}</small>
          </span>
          <span className={`sticky-rugcheck-badge ${active ? "ok" : "soon"}`}>
            {active ? "OPEN" : "SOON"}
          </span>
        </a>
      ))}
    </div>
  );
}


function PriceTicker() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!DEXSCREENER.pairAddress) return;
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(
          `https://api.dexscreener.com/latest/dex/tokens/${DEXSCREENER.pairAddress}`
        );
        const json = await res.json();
        const pair = json?.pair || json?.pairs?.[0];
        if (!cancelled && pair) setData(pair);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    };
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const active = !!DEXSCREENER.pairAddress && data && !err;
  const price = active ? Number(data.priceUsd).toFixed(7) : null;
  const change = active ? Number(data.priceChange?.h24 || 0) : null;
  const mcap = active ? Number(data.marketCap || data.fdv || 0) : null;
  const vol = active ? Number(data.volume?.h24 || 0) : null;
  const fmtMoney = (n) => {
    if (n == null || isNaN(n)) return "—";
    if (n >= 1e9) return `$${(n/1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n/1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n/1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  return (
    <div className={`sticky-ticker ${active ? "is-live" : "is-placeholder"}`} data-testid="price-ticker">
      <div className="sticky-ticker-dot" />
      <span className="sticky-ticker-label">{active ? "LIVE" : "PRE-LAUNCH"}</span>
      <div className="sticky-ticker-divider" />
      {active ? (
        <>
          <div className="sticky-ticker-item">
            <span className="sticky-ticker-k">$STICKY</span>
            <span className="sticky-ticker-v">${price}</span>
          </div>
          <div className="sticky-ticker-item">
            <span className="sticky-ticker-k">24h</span>
            <span className={`sticky-ticker-v ${change >= 0 ? "up" : "down"}`}>
              {change >= 0 ? "▲" : "▼"} {Math.abs(change).toFixed(2)}%
            </span>
          </div>
          <div className="sticky-ticker-item">
            <span className="sticky-ticker-k">MCAP</span>
            <span className="sticky-ticker-v">{fmtMoney(mcap)}</span>
          </div>
          <div className="sticky-ticker-item">
            <span className="sticky-ticker-k">VOL</span>
            <span className="sticky-ticker-v">{fmtMoney(vol)}</span>
          </div>
        </>
      ) : (
        <span className="sticky-ticker-placeholder">
          Live price activates at launch
        </span>
      )}
    </div>
  );
}

function LiveChart() {
  const active = !!DEXSCREENER.pairAddress;
  if (!active) {
    return (
      <div className="sticky-chart-placeholder" data-testid="chart-placeholder">
        <LineChart size={36} color="#FFD84D" />
        <p className="sticky-chart-placeholder-title">Live chart activates at launch</p>
        <p className="sticky-chart-placeholder-sub">
          The DexScreener chart will appear here automatically once $STICKY is tradable on Pump.fun / Raydium.
        </p>
      </div>
    );
  }
  return (
    <div className="sticky-chart-frame" data-testid="chart-frame">
      <iframe
        title="DexScreener chart"
        src={`https://dexscreener.com/${DEXSCREENER.chain}/${DEXSCREENER.pairAddress}?embed=1&theme=dark&trades=0&info=0`}
        loading="lazy"
        allow="clipboard-write"
      />
    </div>
  );
}


function FloatingBuy() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 520);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`sticky-floating-cta ${visible ? "is-visible" : ""}`}
      data-testid="floating-cta"
      aria-hidden={!visible}
    >
      <div className="sticky-floating-cta-inner">
        <img
          src={ASSETS.coinGold}
          alt="Sticky coin"
          className="sticky-floating-coin"
        />
        <span className="sticky-floating-title">STICKY</span>
        <a
          href={LINKS.buy}
          target="_blank"
          rel="noopener noreferrer"
          className="sticky-floating-btn"
          data-testid="floating-buy-btn"
        >
          <Zap size={14} strokeWidth={2.6} />
          <span>Buy</span>
        </a>
      </div>
    </div>
  );
}

function ContactCopyChip({ email }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement("textarea");
        ta.value = email;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button
      type="button"
      className={`sticky-contact-copy ${copied ? "is-copied" : ""}`}
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy email"}
      data-testid="contact-modal-copy"
    >
      {copied ? <Check size={12} strokeWidth={3} /> : <Copy size={12} strokeWidth={2.5} />}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}


function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while contact modal is open + close on Escape
  useEffect(() => {
    if (!contactOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setContactOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [contactOpen]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el =
      document.querySelector(`[data-testid="section-${id}"]`) ||
      document.querySelector(`[data-testid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openContact = (e) => {
    e.preventDefault();
    setOpen(false);
    setContactOpen(true);
  };

  return (
    <>
      <header className={`sticky-header ${scrolled ? "is-scrolled" : ""}`} data-testid="sticky-header">
        <div className="sticky-header-inner">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); setOpen(false); }} className="sticky-header-brand" data-testid="nav-brand">
            <img src={ASSETS.coinGold} alt="" className="sticky-header-logo" />
            <span>STICKY</span>
          </a>
          <nav className="sticky-header-nav">
            {NAV_ITEMS.map((item) =>
              item.type === "route" ? (
                <Link key={item.to} to={item.to} className="sticky-header-link" data-testid={`nav-route-${item.to.replace(/\//g, "-")}`}>
                  {item.label}
                </Link>
              ) : (
                <a key={item.id} href={`#${item.id}`} onClick={go(item.id)} className="sticky-header-link" data-testid={`nav-${item.id}`}>
                  {item.label}
                </a>
              )
            )}
          </nav>
          <a href={LINKS.buy} target="_blank" rel="noopener noreferrer" className="sticky-header-cta" data-testid="nav-buy">
            <Zap size={13} strokeWidth={2.6} />
            <span>Buy Sticky</span>
          </a>
          <button
            type="button"
            className="sticky-header-contact"
            onClick={openContact}
            aria-label="Contact us"
            data-testid="nav-contact-desktop"
          >
            <Mail size={13} strokeWidth={2.4} />
            <span>Contact</span>
          </button>
          <button
            className="sticky-header-burger"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="nav-burger"
          >
            {open ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>
      <div className={`sticky-mobile-menu ${open ? "is-open" : ""}`} data-testid="mobile-menu" aria-hidden={!open}>
        {NAV_ITEMS.map((item) =>
          item.type === "route" ? (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="sticky-mobile-menu-link"
              data-testid={`mobile-nav-route-${item.to.replace(/\//g, "-")}`}
            >
              {item.label}
            </Link>
          ) : (
            <a key={item.id} href={`#${item.id}`} onClick={go(item.id)} className="sticky-mobile-menu-link" data-testid={`mobile-nav-${item.id}`}>
              {item.label}
            </a>
          )
        )}
        <a href={LINKS.buy} target="_blank" rel="noopener noreferrer" className="sticky-mobile-menu-cta" onClick={() => setOpen(false)} data-testid="mobile-nav-buy">
          <Zap size={14} strokeWidth={2.6} />
          <span>Buy Sticky</span>
        </a>
        <div className="sticky-mobile-menu-divider" aria-hidden="true" />
        <button
          type="button"
          className="sticky-mobile-menu-contact"
          onClick={openContact}
          data-testid="mobile-nav-contact"
        >
          <Mail size={16} strokeWidth={2.2} />
          <span>Contact Us</span>
        </button>
      </div>
      {open && <div className="sticky-mobile-menu-backdrop" onClick={() => setOpen(false)} />}

      {/* ============== CONTACT US MODAL ============== */}
      {contactOpen && (
        <div
          className="sticky-contact-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-modal-title"
          data-testid="contact-modal"
          onClick={() => setContactOpen(false)}
        >
          <div className="sticky-contact-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="sticky-contact-close"
              onClick={() => setContactOpen(false)}
              aria-label="Close"
              data-testid="contact-modal-close"
            >
              <XIcon size={18} />
            </button>
            <div className="sticky-contact-icon" aria-hidden="true">
              <Mail size={22} />
            </div>
            <h3 id="contact-modal-title" className="sticky-contact-title">
              Something on your mind? <span className="sticky-contact-eyes" aria-hidden="true">👀</span>
            </h3>
            <p className="sticky-contact-text">
              Let's make it stick. Get in touch with the <span className="sticky-accent">STICKY</span> team!
            </p>
            <a
              href="mailto:support@stickylikeyou.com?subject=Hello%20STICKY%20Team"
              className="sticky-contact-btn"
              data-testid="contact-modal-btn"
              onClick={() => setContactOpen(false)}
            >
              <Mail size={14} strokeWidth={2.5} />
              <span>Contact us</span>
            </a>
            <div className="sticky-contact-email-wrap">
              <p className="sticky-contact-email" data-testid="contact-modal-email">
                support@stickylikeyou.com
              </p>
              <ContactCopyChip email="support@stickylikeyou.com" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AppCarousel() {
  const [i, setI] = useState(0);
  const total = APP_SCREENSHOTS.length;
  const startX = useRef(0);
  const deltaX = useRef(0);

  const prev = () => setI((v) => (v - 1 + total) % total);
  const next = () => setI((v) => (v + 1) % total);

  const onStart = (e) => { startX.current = e.touches[0].clientX; deltaX.current = 0; };
  const onMove = (e) => { deltaX.current = e.touches[0].clientX - startX.current; };
  const onEnd = () => {
    if (Math.abs(deltaX.current) > 50) (deltaX.current < 0 ? next : prev)();
  };

  return (
    <div className="sticky-carousel" data-testid="app-carousel">
      <div className="sticky-carousel-viewport"
           onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}>
        <div className="sticky-carousel-track" style={{ transform: `translateX(-${i * 100}%)` }}>
          {APP_SCREENSHOTS.map((s, idx) => (
            <div className="sticky-carousel-slide" key={idx}>
              <div className="sticky-carousel-phone">
                <div className="sticky-carousel-notch" />
                <img src={s.src} alt={s.label} loading="lazy" />
              </div>
            </div>
          ))}
        </div>
        <button className="sticky-carousel-arrow sticky-carousel-arrow-l"
                onClick={prev} aria-label="Previous" data-testid="carousel-prev">
          <ChevronLeft size={18} />
        </button>
        <button className="sticky-carousel-arrow sticky-carousel-arrow-r"
                onClick={next} aria-label="Next" data-testid="carousel-next">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="sticky-carousel-dots" data-testid="carousel-dots">
        {APP_SCREENSHOTS.map((_, idx) => (
          <button
            key={idx}
            className={`sticky-carousel-dot ${idx === i ? "is-active" : ""}`}
            onClick={() => setI(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            data-testid={`carousel-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
}

function HiringAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sticky-hiring ${open ? "is-open" : ""}`} data-testid="hiring-accordion">
      <button
        type="button"
        className="sticky-hiring-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="hiring-toggle"
      >
        <div className="sticky-hiring-head-left">
          <span className="sticky-hiring-icon">🚀</span>
          <div>
            <p className="sticky-hiring-tag">Careers · StickyEcosystem</p>
            <h3 className="sticky-hiring-title">We're Hiring</h3>
          </div>
        </div>
        <ChevronDown size={18} className="sticky-hiring-chev" />
      </button>
      <div className="sticky-hiring-body">
        <p className="sticky-hiring-lead">
          Become part of <strong>StickyEcosystem</strong>.
        </p>
        <p className="sticky-hiring-p">
          We're looking for <strong>Social Media Marketers</strong> &{" "}
          <strong>Community Ambassadors</strong> who want to help grow StickyLikeYou.
        </p>
        <p className="sticky-hiring-p sticky-hiring-slogan">
          Spread the word. Grow with us.
        </p>

        <p className="sticky-hiring-p">
          If you want to join our team, send us an email at{" "}
          <a href="mailto:support@stickylikeyou.com" className="sticky-hiring-mail" data-testid="hiring-email">
            support@stickylikeyou.com
          </a>{" "}
          with:
        </p>

        <ul className="sticky-hiring-list">
          <li>Your name</li>
          <li>Your social media profiles</li>
          <li>Your experience in marketing or community building</li>
          <li>Why you want to join StickyEcosystem</li>
          <li>How you can help us grow</li>
        </ul>

        <p className="sticky-hiring-outro">
          Let's make Sticky stick. <span className="sticky-hiring-dot">🟡</span>
        </p>

        <a
          href="mailto:support@stickylikeyou.com?subject=Join%20StickyEcosystem%20Team"
          className="sticky-btn sticky-btn-primary sticky-hiring-cta"
          data-testid="hiring-apply-btn"
        >
          <Send size={16} strokeWidth={2.4} />
          <span>Apply Now</span>
        </a>
      </div>
    </div>
  );
}


function RoadmapAccordion({ phases }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="sticky-roadmap" data-testid="roadmap">
      {phases.map((p, idx) => {
        const isOpen = open === idx;
        return (
          <div className={`sticky-phase sticky-phase-acc ${isOpen ? "is-open" : ""}`} key={idx} data-testid={`phase-${idx + 1}`}>
            <button
              type="button"
              className="sticky-phase-head"
              onClick={() => setOpen(isOpen ? -1 : idx)}
              aria-expanded={isOpen}
              data-testid={`phase-${idx + 1}-toggle`}
            >
              <div>
                <span className="sticky-phase-tag">{p.phase}</span>
                <h3 className="sticky-phase-title">{p.title}</h3>
              </div>
              <ChevronDown size={18} className="sticky-phase-chev" />
            </button>
            <div className="sticky-phase-body">
              <ul>
                {p.items.map((it) => (
                  <li key={it}>{it}</li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContestCountdown({ endAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, endAt - now);
  const ended = ms === 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div
      className={`sticky-contest-countdown ${ended ? "is-ended" : ""}`}
      data-testid="round2-countdown"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="sticky-contest-countdown-label">
        {ended ? "Contest ended" : "Ends in"}
      </span>
      {!ended && (
        <span className="sticky-contest-countdown-time" data-testid="round2-countdown-time">
          {pad(h)}h : {pad(m)}m : {pad(s)}s
        </span>
      )}
    </div>
  );
}

function MemeContestsCard({ Icon, title, desc }) {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState({ r1: false, r2: false, cc: false });
  const winners = [
    { medal: "🥇", place: "1st", prize: "$20", handle: "el_bicho99",    cls: "gold" },
    { medal: "🥈", place: "2nd", prize: "$10", handle: "itzchellacrypt", cls: "silver" },
    { medal: "🥉", place: "3rd", prize: "$5",  handle: "adosterone",     cls: "bronze" },
  ];
  return (
    <div
      className={`sticky-card sticky-card-compact sticky-meme-contest-card ${open ? "is-open" : ""}`}
      data-testid={`community-${title}`}
    >
      <button
        type="button"
        className="sticky-meme-contest-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="meme-contest-winners-panel"
        data-testid="meme-contests-toggle"
      >
        <div className="sticky-card-icon sticky-card-icon-sm"><Icon size={16} /></div>
        <div className="sticky-card-compact-body">
          <p className="sticky-card-title sticky-card-title-sm">
            {title}
            <span className="sticky-meme-contest-live" aria-label="Live activity">
              <span className="sticky-meme-contest-live-dot" />
            </span>
          </p>
          <p className="sticky-card-text sticky-card-text-sm">{desc}</p>
        </div>
        <ChevronDown
          size={18}
          className={`sticky-meme-contest-chevron ${open ? "is-open" : ""}`}
        />
      </button>

      <div
        id="meme-contest-winners-panel"
        className={`sticky-meme-contest-panel ${open ? "is-open" : ""}`}
        data-testid="meme-contest-winners"
      >
        <div className="sticky-meme-subs">

          {/* ---------- SUB 1: Meme Contest — Round 1 (Finished) ---------- */}
          <div className={`sticky-meme-sub ${sub.r1 ? "is-open" : ""}`} data-testid="sub-round1">
            <button
              type="button"
              className="sticky-meme-sub-trigger"
              onClick={(e) => { e.stopPropagation(); setSub((s) => ({ ...s, r1: !s.r1 })); }}
              aria-expanded={sub.r1}
              data-testid="sub-round1-toggle"
            >
              <span className="sticky-meme-sub-icon"><Trophy size={14} /></span>
              <span className="sticky-meme-sub-titles">
                <span className="sticky-meme-sub-title">Meme Contest — <b>Round 1</b></span>
                <span className="sticky-meme-sub-status is-finished">Finished · 18.09 — 20.09.2026</span>
              </span>
              <ChevronDown size={16} className={`sticky-meme-sub-chev ${sub.r1 ? "is-open" : ""}`} />
            </button>
            <div className={`sticky-meme-sub-panel ${sub.r1 ? "is-open" : ""}`}>
              <div className="sticky-winners-inner">
                <div className="sticky-winners-header">
                  <p className="sticky-winners-subtitle">
                    <span className="sticky-trophy-emoji">🏆</span> TOP 3 WINNERS
                  </p>
                </div>

                <ul className="sticky-winners-list" data-testid="winners-list">
                  {winners.map((w) => (
                    <li
                      key={w.handle}
                      className={`sticky-winner-row sticky-winner-${w.cls}`}
                      data-testid={`winner-${w.place}`}
                    >
                      <span className="sticky-winner-medal" aria-hidden="true">{w.medal}</span>
                      <span className="sticky-winner-place">{w.place}</span>
                      <span className="sticky-winner-dash">—</span>
                      <span className="sticky-winner-prize">{w.prize}</span>
                      <a
                        href={`https://x.com/${w.handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sticky-winner-handle"
                        data-testid={`winner-link-${w.handle}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        @{w.handle}
                        <ExternalLink size={12} />
                      </a>
                    </li>
                  ))}
                </ul>

                <div className="sticky-winners-footer">
                  <p className="sticky-winners-congrats">
                    <span className="sticky-clap">🎉</span> Congratulations to all 3 winners!
                  </p>
                  <p className="sticky-winners-paid">
                    <BadgeCheck size={14} color="#FFD84D" />
                    All prizes have been successfully distributed!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ---------- SUB 2: STICKY Meme Contest — Round 2 (Coming Soon) ---------- */}
          <div className={`sticky-meme-sub ${sub.r2 ? "is-open" : ""}`} data-testid="sub-round2">
            <button
              type="button"
              className="sticky-meme-sub-trigger"
              onClick={(e) => { e.stopPropagation(); setSub((s) => ({ ...s, r2: !s.r2 })); }}
              aria-expanded={sub.r2}
              data-testid="sub-round2-toggle"
            >
              <span className="sticky-meme-sub-icon"><Gift size={14} /></span>
              <span className="sticky-meme-sub-titles">
                <span className="sticky-meme-sub-title">STICKY Meme Contest — <b>Round 2</b> · 500,000 STICKY Airdrop</span>
                <span className="sticky-meme-sub-status is-live" data-testid="round2-badge">LIVE</span>
              </span>
              <ChevronDown size={16} className={`sticky-meme-sub-chev ${sub.r2 ? "is-open" : ""}`} />
            </button>
            <div className={`sticky-meme-sub-panel ${sub.r2 ? "is-open" : ""}`}>
              <div className="sticky-winners-inner">
                <ContestCountdown endAt={CONTEST_R2_END_AT} />
                <div className="sticky-winners-header">
                  <p className="sticky-winners-subtitle">
                    <span className="sticky-trophy-emoji">🏆</span> 35 Winners · 500,000 STICKY Total
                  </p>
                </div>

                <ul className="sticky-winners-list" data-testid="round2-prizes">
                  <li className="sticky-winner-row sticky-winner-gold" data-testid="round2-tier-1">
                    <span className="sticky-winner-medal" aria-hidden="true">🥇</span>
                    <span className="sticky-winner-place">1 winner</span>
                    <span className="sticky-winner-dash">—</span>
                    <span className="sticky-winner-prize">100,000 STICKY</span>
                  </li>
                  <li className="sticky-winner-row sticky-winner-silver" data-testid="round2-tier-2">
                    <span className="sticky-winner-medal" aria-hidden="true">🥈</span>
                    <span className="sticky-winner-place">4 winners</span>
                    <span className="sticky-winner-dash">—</span>
                    <span className="sticky-winner-prize">50,000 STICKY each</span>
                  </li>
                  <li className="sticky-winner-row sticky-winner-bronze" data-testid="round2-tier-3">
                    <span className="sticky-winner-medal" aria-hidden="true">🥉</span>
                    <span className="sticky-winner-place">10 winners</span>
                    <span className="sticky-winner-dash">—</span>
                    <span className="sticky-winner-prize">20,000 STICKY each</span>
                  </li>
                  <li className="sticky-winner-row sticky-winner-bronze" data-testid="round2-tier-4">
                    <span className="sticky-winner-medal" aria-hidden="true">🎁</span>
                    <span className="sticky-winner-place">20 winners</span>
                    <span className="sticky-winner-dash">—</span>
                    <span className="sticky-winner-prize">10,000 STICKY each</span>
                  </li>
                </ul>

                <div className="sticky-winners-footer">
                  <a
                    href="https://tally.so/r/44eQ4B"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sticky-mini-btn"
                    data-testid="round2-enter-btn"
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: 6 }}
                  >
                    <Rocket size={14} strokeWidth={2.6} />
                    <span>Enter the Contest</span>
                    <ExternalLink size={12} />
                  </a>
                  <p className="sticky-winners-paid" style={{ marginTop: 10 }}>
                    <BadgeCheck size={14} color="#FFD84D" />
                    Payouts will be distributed after $STICKY launches on Solana.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ---------- SUB 3: STICKY Community Campaign — Round 1 (Coming Soon) ---------- */}
          <div className={`sticky-meme-sub ${sub.cc ? "is-open" : ""}`} data-testid="sub-campaign">
            <button
              type="button"
              className="sticky-meme-sub-trigger"
              onClick={(e) => { e.stopPropagation(); setSub((s) => ({ ...s, cc: !s.cc })); }}
              aria-expanded={sub.cc}
              data-testid="sub-campaign-toggle"
            >
              <span className="sticky-meme-sub-icon"><Sparkles size={14} /></span>
              <span className="sticky-meme-sub-titles">
                <span className="sticky-meme-sub-title">STICKY Community Campaign — <b>Round 1</b></span>
                <span className="sticky-meme-sub-status is-soon">Coming Soon</span>
              </span>
              <ChevronDown size={16} className={`sticky-meme-sub-chev ${sub.cc ? "is-open" : ""}`} />
            </button>
            <div className={`sticky-meme-sub-panel ${sub.cc ? "is-open" : ""}`}>
              <div className="sticky-winners-inner">
                <div className="sticky-winners-header">
                  <p className="sticky-winners-subtitle">
                    <span className="sticky-trophy-emoji">🏆</span> Everyone Is a Winner
                  </p>
                </div>

                <div className="sticky-campaign-tease">
                  <p className="sticky-campaign-tease-text">
                    A community-first round where <strong>every participant</strong> takes something home.
                  </p>
                  <p className="sticky-campaign-tease-sub">
                    Details, tasks and rewards drop soon — stay sticky. 🍯
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}



function GiveawayCard({ Icon, title, desc }) {
  const [open, setOpen] = useState(false);

  // Campaign window
  const startsAt = new Date("2026-09-22T15:15:00Z");
  const endsAt   = new Date("2026-09-25T23:59:00Z");
  const now = new Date();

  let status = "upcoming";
  if (now >= startsAt && now <= endsAt) status = "live";
  else if (now > endsAt)                status = "ended";

  const statusLabel = {
    upcoming: "Upcoming",
    live: "Live Now",
    ended: "Ended",
  }[status];

  const perks = ["Radio", "Crypto News", "Community", "Rewards", "AI & More"];
  const stats = [
    { i: Gift,     v: "$25",  l: "Total Prizes" },
    { i: Trophy,   v: "5",    l: "Winners" },
    { i: Sparkles, v: "$5",   l: "Each" },
  ];

  return (
    <div
      className={`sticky-card sticky-card-compact sticky-giveaway-card ${open ? "is-open" : ""}`}
      data-testid={`community-${title}`}
    >
      <button
        type="button"
        className="sticky-meme-contest-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="giveaway-panel"
        data-testid="giveaway-toggle"
      >
        <div className="sticky-card-icon sticky-card-icon-sm"><Icon size={16} /></div>
        <div className="sticky-card-compact-body">
          <p className="sticky-card-title sticky-card-title-sm">
            {title}
            <span className={`sticky-meme-contest-tag sticky-giveaway-tag-${status}`}>
              $25 · {statusLabel}
            </span>
          </p>
          <p className="sticky-card-text sticky-card-text-sm">{desc}</p>
        </div>
        <ChevronDown
          size={18}
          className={`sticky-meme-contest-chevron ${open ? "is-open" : ""}`}
        />
      </button>

      <div
        id="giveaway-panel"
        className={`sticky-meme-contest-panel ${open ? "is-open" : ""}`}
        data-testid="giveaway-panel"
      >
        <div className="sticky-winners-inner sticky-giveaway-inner">
          <div className="sticky-winners-header">
            <div className={`sticky-winners-badge sticky-giveaway-status-${status}`}>
              <Sparkles size={14} />
              <span>{statusLabel}</span>
            </div>
            <h3 className="sticky-winners-title">
              <span className="sticky-giveaway-emoji">🎉</span>
              STICKY <span className="sticky-accent">$25</span> Giveaway
              <span className="sticky-giveaway-emoji sticky-fire">🔥</span>
            </h3>
            <p className="sticky-giveaway-tagline">
              Want a piece of the STICKY Ecosystem?
            </p>
            <div className="sticky-giveaway-perks" aria-label="Ecosystem perks">
              <span className="sticky-giveaway-globe" aria-hidden="true">🌎</span>
              {perks.map((p, idx) => (
                <React.Fragment key={p}>
                  <span className="sticky-giveaway-perk">{p}</span>
                  {idx < perks.length - 1 && <span className="sticky-giveaway-dot">•</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="sticky-giveaway-stats" data-testid="giveaway-stats">
            {stats.map(({ i: Ic, v, l }) => (
              <div key={l} className="sticky-giveaway-stat">
                <div className="sticky-giveaway-stat-icon"><Ic size={16} /></div>
                <div className="sticky-giveaway-stat-value">{v}</div>
                <div className="sticky-giveaway-stat-label">{l}</div>
              </div>
            ))}
          </div>

          <div className="sticky-giveaway-window" data-testid="giveaway-window">
            <div className="sticky-giveaway-window-row">
              <span className="sticky-giveaway-window-label">Starts</span>
              <span className="sticky-giveaway-window-value">22 Sep 2026 · 03:15 PM</span>
            </div>
            <div className="sticky-giveaway-window-divider" />
            <div className="sticky-giveaway-window-row">
              <span className="sticky-giveaway-window-label">Ends</span>
              <span className="sticky-giveaway-window-value">25 Sep 2026 · 11:59 PM</span>
            </div>
          </div>

          <p className="sticky-giveaway-cta-text">
            {status === "ended"
              ? "This round is complete. Thank you to everyone who joined! 🙏"
              : "Complete the tasks, collect your entries and get your chance to win! 🚀"}
          </p>

          {status === "ended" ? (
            <div className="sticky-giveaway-winners" data-testid="giveaway-winners">
              <div className="sticky-giveaway-winners-header">
                <span className="sticky-giveaway-winners-crown" aria-hidden="true">👑</span>
                <span className="sticky-giveaway-winners-title-text">
                  5 Winners · $5 Each
                </span>
              </div>

              <ul className="sticky-giveaway-winners-list" data-testid="giveaway-winners-list">
                {[
                  { place: 1, medal: "🥇", entries: 460,  name: "Narcis B*****",     action: "Visit StickyLikeyou on YouTube", when: "23 Sep · 22:03" },
                  { place: 2, medal: "🥈", entries: 1068, name: "Mercedes D********", action: "Visit StickyLikeyou on YouTube", when: "25 Sep · 00:32" },
                  { place: 3, medal: "🥉", entries: 478,  name: "Thanh N*********",   action: "Visit Sticky Ecosystem site",   when: "23 Sep · 22:44" },
                  { place: 4, medal: "🏅", entries: 38,   name: "@el_bicho99",        action: "Repost @StickyEcosystem on X",  when: "22 Sep · 23:53", handle: "el_bicho99" },
                  { place: 5, medal: "🏅", entries: 172,  name: "Walter R***",        action: "Visit StickyLikeyou on YouTube", when: "23 Sep · 11:04" },
                ].map((w) => (
                  <li
                    key={w.place}
                    className={`sticky-giveaway-winner-row sticky-giveaway-winner-${w.place <= 3 ? "top" : "runner"}`}
                    data-testid={`giveaway-winner-${w.place}`}
                  >
                    <span className="sticky-giveaway-winner-medal" aria-hidden="true">{w.medal}</span>
                    <span className="sticky-giveaway-winner-info">
                      <span className="sticky-giveaway-winner-name">
                        {w.handle ? (
                          <a
                            href={`https://x.com/${w.handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sticky-giveaway-winner-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {w.name}
                          </a>
                        ) : w.name}
                      </span>
                      <span className="sticky-giveaway-winner-meta">
                        <span className="sticky-giveaway-winner-entries">{w.entries.toLocaleString()} entries</span>
                        <span className="sticky-giveaway-winner-sep">•</span>
                        <span className="sticky-giveaway-winner-action">{w.action}</span>
                      </span>
                    </span>
                    <span className="sticky-giveaway-winner-prize">$5</span>
                  </li>
                ))}
              </ul>

              <div className="sticky-giveaway-winners-footer">
                <p className="sticky-giveaway-winners-congrats">
                  <span className="sticky-clap">🎉</span> Congrats to all 5 winners!
                </p>
                <p className="sticky-giveaway-winners-paid">
                  <BadgeCheck size={14} color="#FFD84D" />
                  All prizes have been successfully distributed.
                </p>
              </div>
            </div>
          ) : (
            <>
              <a
                href="https://gleam.io/APkNU/instant-entry"
                target="_blank"
                rel="noopener noreferrer"
                className="sticky-giveaway-btn"
                data-testid="giveaway-enter-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sticky-giveaway-btn-arrow" aria-hidden="true">👇</span>
                Enter Here
                <ExternalLink size={14} />
              </a>
              <p className="sticky-giveaway-powered">Powered by Gleam</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MARKETS SECTION — Live Top 20 (CoinGecko + DexScreener)
   ============================================================ */
const fmtPrice = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1000) return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (n >= 1)    return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (n >= 0.01) return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 4 });
  return "$" + n.toPrecision(2);
};
const fmtCompact = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T";
  if (n >= 1e9)  return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6)  return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3)  return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toFixed(0);
};
const fmtPct = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return sign + n.toFixed(2) + "%";
};

function useCountdown(seconds) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const id = setInterval(() => setLeft((v) => (v <= 1 ? seconds : v - 1)), 1000);
    return () => clearInterval(id);
  }, [seconds]);
  return left;
}

function MarketTable({ title, subtitle, items, loading, error, columns, testId }) {
  return (
    <div className="sticky-market-table" data-testid={testId}>
      <div className="sticky-market-head">
        <h3 className="sticky-market-title">{title}</h3>
        <p className="sticky-market-sub">{subtitle}</p>
      </div>

      {loading && !items?.length && (
        <div className="sticky-market-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="sticky-market-skel-row" />
          ))}
        </div>
      )}
      {error && !items?.length && (
        <p className="sticky-market-error" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
      {!!items?.length && (
        <div className="sticky-market-scroll">
          <table className="sticky-market-grid">
            <thead>
              <tr>
                <th className="sticky-market-th-num">#</th>
                <th>Asset</th>
                {columns.map((c) => (
                  <th key={c.key} className={`sticky-market-th-${c.align || "right"}`}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id || idx} data-testid={`${testId}-row-${idx + 1}`}>
                  <td className="sticky-market-num">{idx + 1}</td>
                  <td>
                    <a
                      href={row.url || "#"}
                      target={row.url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="sticky-market-asset"
                    >
                      {row.img ? (
                        <img src={row.img} alt="" loading="lazy" />
                      ) : (
                        <span className="sticky-market-dot" />
                      )}
                      <span className="sticky-market-asset-text">
                        <span className="sticky-market-symbol">{row.symbol}</span>
                        {row.name && <span className="sticky-market-name">{row.name}</span>}
                      </span>
                    </a>
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className={`sticky-market-td-${c.align || "right"} ${c.cls?.(row) || ""}`}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   FOMO Trader Leaderboard — Backend-proxied cache, tabs 24H/7D/30D
   ============================================================ */
const FOMO_WINDOWS = [
  { key: "24h", label: "24H" },
  { key: "7d",  label: "7D"  },
  { key: "30d", label: "30D" },
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function fetchFomoWindow(window) {
  const res = await fetch(`${BACKEND_URL}/api/fomo/leaderboard/${window}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function useFomoLeaderboard() {
  const [win, setWin] = useState("24h");
  const [byWin, setByWin] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (w) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFomoWindow(w);
      setByWin((s) => ({ ...s, [w]: data }));
    } catch (e) {
      setError("Leaderboard temporarily unavailable. Retrying…");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(win);
    // Frontend re-checks cache every 5 min (backend still caps upstream at 6h)
    const id = setInterval(() => load(win), 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win]);

  return {
    win,
    setWin,
    data: byWin[win],
    loading,
    error,
  };
}

function WalletCopyChip({ address, chain = "sol" }) {
  const [copied, setCopied] = useState(false);
  if (!address) return null;
  const short = address.length > 8 ? `${address.slice(0, 4)}…${address.slice(-4)}` : address;
  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(address);
      } else {
        const ta = document.createElement("textarea");
        ta.value = address;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button
      type="button"
      className={`sticky-fomo-wallet-chip ${copied ? "is-copied" : ""}`}
      onClick={onCopy}
      aria-label={copied ? "Wallet copied" : `Copy ${chain.toUpperCase()} wallet ${short}`}
      title={address}
      data-testid={`wallet-copy-${chain}`}
    >
      {copied ? <Check size={11} strokeWidth={3} /> : <Copy size={11} strokeWidth={2.5} />}
      <span className="sticky-fomo-wallet-chip-text">{copied ? "Copied" : short}</span>
    </button>
  );
}


function FomoTraderCell({ t }) {
  const label = t.displayName || t.handle || (t.wallets?.solana?.slice(0, 4) + "…" + t.wallets?.solana?.slice(-4)) || "—";
  const solWallet = t.wallets?.solana || null;
  const trader = (
    <span className="sticky-fomo-trader">
      {t.avatar ? (
        <img src={t.avatar} alt="" loading="lazy" />
      ) : (
        <span className="sticky-fomo-trader-dot" aria-hidden="true">
          {(label[0] || "?").toUpperCase()}
        </span>
      )}
      <span className="sticky-fomo-trader-text">
        <span className="sticky-fomo-trader-name">
          {label}
          {t.verified && <BadgeCheck size={11} className="sticky-fomo-verified" />}
        </span>
        {t.handle && <span className="sticky-fomo-trader-handle">@{t.handle}</span>}
      </span>
    </span>
  );
  const link = t.profileUrl ? (
    <a href={t.profileUrl} target="_blank" rel="noopener noreferrer" className="sticky-fomo-trader-link">
      {trader}
    </a>
  ) : trader;
  return (
    <span className="sticky-fomo-trader-wrap">
      {link}
      {solWallet && <WalletCopyChip address={solWallet} chain="sol" />}
    </span>
  );
}

const fmtSigned = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  if (Math.abs(v) >= 1e9) return sign + "$" + (v / 1e9).toFixed(2) + "B";
  if (Math.abs(v) >= 1e6) return sign + "$" + (v / 1e6).toFixed(2) + "M";
  if (Math.abs(v) >= 1e3) return sign + "$" + (v / 1e3).toFixed(1) + "K";
  return sign + "$" + v.toFixed(0);
};
const fmtRoi = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : (Number(n) > 0 ? "+" : "") + Number(n).toFixed(2) + "%");
const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));

function FomoLeaderboard() {
  const { win, setWin, data, loading, error } = useFomoLeaderboard();
  const traders = data?.traders || [];
  const fetchedAt = data?.fetchedAt ? new Date(data.fetchedAt) : null;
  const stale = data?.cacheStatus === "stale";

  return (
    <div className="sticky-market-table sticky-fomo-table" data-testid="market-fomo" id="fomo-leaderboard">
      <div className="sticky-fomo-head">
        <div>
          <h3 className="sticky-market-title">
            <span className="sticky-fomo-fire" aria-hidden="true">🔥</span> FOMO Trader Leaderboard
          </h3>
          <p className="sticky-market-sub">Top 20 traders ranked by PnL · Powered by FOMO Family API</p>
        </div>
        <div className="sticky-fomo-tabs" role="tablist" aria-label="Time window">
          {FOMO_WINDOWS.map((w) => (
            <button
              key={w.key}
              role="tab"
              aria-selected={win === w.key}
              className={`sticky-fomo-tab ${win === w.key ? "is-active" : ""}`}
              onClick={() => setWin(w.key)}
              data-testid={`fomo-tab-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !traders.length && (
        <div className="sticky-market-skeleton">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="sticky-market-skel-row" />)}
        </div>
      )}

      {error && !traders.length && (
        <p className="sticky-market-error" data-testid="market-fomo-error">{error}</p>
      )}

      {!!traders.length && (
        <div className="sticky-market-scroll">
          <table className="sticky-market-grid sticky-fomo-grid">
            <thead>
              <tr>
                <th className="sticky-market-th-num">#</th>
                <th>Trader</th>
                <th className="sticky-market-th-right">PnL</th>
                <th className="sticky-market-th-right">ROI</th>
                <th className="sticky-market-th-right">Volume</th>
                <th className="sticky-market-th-right">Trades</th>
              </tr>
            </thead>
            <tbody>
              {traders.map((t, i) => (
                <tr key={t.handle || t.wallets?.solana || i} data-testid={`fomo-row-${i + 1}`}>
                  <td className="sticky-market-num">{t.rank || i + 1}</td>
                  <td><FomoTraderCell t={t} /></td>
                  <td className={`sticky-market-td-right ${t.pnlUsd == null ? "" : t.pnlUsd >= 0 ? "is-up" : "is-down"}`}>
                    {fmtSigned(t.pnlUsd)}
                  </td>
                  <td className={`sticky-market-td-right ${t.roi == null ? "" : t.roi >= 0 ? "is-up" : "is-down"}`}>
                    {fmtRoi(t.roi)}
                  </td>
                  <td className="sticky-market-td-right">{fmtCompact(t.volumeUsd)}</td>
                  <td className="sticky-market-td-right">{fmtInt(t.trades)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="sticky-markets-updated sticky-fomo-updated">
        {stale && <span className="sticky-fomo-stale-tag">Cache · Stale</span>}
        Last updated: {fetchedAt ? fetchedAt.toLocaleString("en-US", { dateStyle: "short", timeStyle: "medium" }) : "—"}
        <span className="sticky-fomo-refresh-note">· auto-refresh every 6h</span>
      </p>
    </div>
  );
}

function FomoTop5Preview() {
  const { data, loading, error } = useFomoLeaderboard();
  const top = (data?.traders || []).slice(0, 5);
  const goToFull = (e) => {
    e.preventDefault();
    const el = document.getElementById("fomo-leaderboard");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else {
      const mk = document.querySelector('[data-testid="section-markets"]');
      if (mk) mk.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div className="sticky-fomo-preview" data-testid="fomo-top5-preview">
      <div className="sticky-fomo-preview-head">
        <div>
          <h3 className="sticky-fomo-preview-title">
            <span className="sticky-fomo-fire" aria-hidden="true">🔥</span> FOMO Traders · Top 5 (24H)
          </h3>
          <p className="sticky-fomo-preview-sub">Live from FOMO Family API</p>
        </div>
      </div>

      {loading && !top.length && (
        <div className="sticky-market-skeleton">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="sticky-market-skel-row" />)}
        </div>
      )}
      {error && !top.length && (
        <p className="sticky-market-error">{error}</p>
      )}
      {!!top.length && (
        <ul className="sticky-fomo-preview-list" data-testid="fomo-top5-list">
          {top.map((t, i) => (
            <li key={t.handle || i} className="sticky-fomo-preview-row" data-testid={`fomo-top5-row-${i + 1}`}>
              <span className="sticky-fomo-preview-rank">{t.rank || i + 1}</span>
              <span className="sticky-fomo-preview-trader"><FomoTraderCell t={t} /></span>
              <span className={`sticky-fomo-preview-pnl ${t.pnlUsd == null ? "" : t.pnlUsd >= 0 ? "is-up" : "is-down"}`}>
                {fmtSigned(t.pnlUsd)}
              </span>
              <span className={`sticky-fomo-preview-roi ${t.roi == null ? "" : t.roi >= 0 ? "is-up" : "is-down"}`}>
                {fmtRoi(t.roi)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <a
        href="#markets"
        onClick={goToFull}
        className="sticky-fomo-preview-cta"
        data-testid="fomo-top5-view-full"
      >
        <span>View Full Leaderboard</span>
        <ChevronRight size={14} strokeWidth={2.6} />
      </a>
    </div>
  );
}


function MarketsSection() {
  const REFRESH_MS = 60_000;
  const [cg, setCg]           = useState({ items: [], loading: true, error: null, updatedAt: null });
  const [dex, setDex]         = useState({ items: [], loading: true, error: null, updatedAt: null });
  const [nextTick, setNextTick] = useState(60);

  // ---- CoinGecko: Top 20 by market cap ----
  const loadCg = async () => {
    try {
      const url =
        "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=20&page=1&sparkline=false&price_change_percentage=24h";
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const json = await res.json();
      const items = json.map((c) => ({
        id: c.id,
        symbol: (c.symbol || "").toUpperCase(),
        name: c.name,
        img: c.image,
        price: c.current_price,
        change24: c.price_change_percentage_24h,
        mcap: c.market_cap,
        url: `https://www.coingecko.com/en/coins/${c.id}`,
      }));
      setCg({ items, loading: false, error: null, updatedAt: new Date() });
    } catch (e) {
      setCg((s) => ({ ...s, loading: false, error: "Live data temporarily unavailable. Retrying…" }));
    }
  };

  // ---- DexScreener: Top boosted → enrich pairs → top 20 by volume ----
  const loadDex = async () => {
    try {
      // 1) Get trending metas (categories sorted by 24h trending activity)
      const trendRes = await fetch("https://api.dexscreener.com/metas/trending/v1", {
        headers: { Accept: "application/json" },
      });
      if (!trendRes.ok) throw new Error("HTTP " + trendRes.status);
      const metas = await trendRes.json(); // array of metas
      // Sort by 24h volume as the "trending 24h" ranking
      const sortedMetas = [...metas]
        .filter((m) => m && m.slug)
        .sort((a, b) => (b.volume || 0) - (a.volume || 0));
      // 2) Fetch pairs from top trending metas in parallel (limit to keep under rate cap)
      const topMetas = sortedMetas.slice(0, 6);
      const metaResults = await Promise.all(
        topMetas.map(async (m) => {
          try {
            const r = await fetch(
              `https://api.dexscreener.com/metas/meta/v1/${encodeURIComponent(m.slug)}`,
              { headers: { Accept: "application/json" } }
            );
            if (!r.ok) return [];
            const data = await r.json();
            return Array.isArray(data?.pairs) ? data.pairs : [];
          } catch {
            return [];
          }
        })
      );
      const allPairs = metaResults.flat();
      // Keep best pair per baseToken (highest volume)
      const bestByToken = {};
      allPairs.forEach((p) => {
        if (!p?.baseToken?.address) return;
        const key = `${p.chainId}:${p.baseToken.address}`;
        const vol = p.volume?.h24 || 0;
        if (!bestByToken[key] || (bestByToken[key].volume?.h24 || 0) < vol) {
          bestByToken[key] = p;
        }
      });
      const top20 = Object.values(bestByToken)
        .sort((a, b) => (b.volume?.h24 || 0) - (a.volume?.h24 || 0))
        .slice(0, 20);
      const items = top20.map((p) => ({
        id: `${p.chainId}-${p.baseToken.address}`,
        symbol: (p.baseToken.symbol || "").toUpperCase(),
        name: p.baseToken.name,
        img: p.info?.imageUrl || null,
        chain: p.chainId,
        price: parseFloat(p.priceUsd) || null,
        change24: p.priceChange?.h24 ?? null,
        volume: p.volume?.h24 || 0,
        liquidity: p.liquidity?.usd || 0,
        url: p.url,
      }));
      setDex({ items, loading: false, error: null, updatedAt: new Date() });
    } catch (e) {
      setDex((s) => ({ ...s, loading: false, error: "Live data temporarily unavailable. Retrying…" }));
    }
  };

  useEffect(() => {
    loadCg();
    loadDex();
    const id = setInterval(() => {
      loadCg();
      loadDex();
      setNextTick(60);
    }, REFRESH_MS);
    const tick = setInterval(() => setNextTick((v) => (v <= 1 ? 60 : v - 1)), 1000);
    return () => { clearInterval(id); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastUpdated = (d) =>
    d ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

  return (
    <Section id="markets" eyebrow="Markets" title="Live Markets" accentWord="Live">
      <div className="sticky-markets-meta" data-testid="markets-meta">
        <span className="sticky-markets-live-dot" aria-hidden="true" />
        <span className="sticky-markets-live-label">Live · auto-refresh</span>
        <span className="sticky-markets-tick">next refresh in <b>{nextTick}s</b></span>
      </div>

      <MarketTable
        testId="market-cg"
        title="Global Top 20"
        subtitle="Top 20 crypto assets by market capitalization · Powered by CoinGecko market data"
        items={cg.items}
        loading={cg.loading}
        error={cg.error}
        columns={[
          {
            key: "price",
            header: "Price",
            render: (r) => fmtPrice(r.price),
          },
          {
            key: "change",
            header: "24h",
            cls: (r) => (r.change24 == null ? "" : r.change24 >= 0 ? "is-up" : "is-down"),
            render: (r) => fmtPct(r.change24),
          },
          {
            key: "mcap",
            header: "Market Cap",
            render: (r) => fmtCompact(r.mcap),
          },
        ]}
      />
      <p className="sticky-markets-updated">Last updated: {lastUpdated(cg.updatedAt)}</p>

      <MarketTable
        testId="market-dex"
        title="DEX Screener Top 20 — Trending 24h"
        subtitle="Real-time trending pairs · Market data powered by DEX Screener"
        items={dex.items}
        loading={dex.loading}
        error={dex.error}
        columns={[
          {
            key: "price",
            header: "Price",
            render: (r) => fmtPrice(r.price),
          },
          {
            key: "change",
            header: "24h",
            cls: (r) => (r.change24 == null ? "" : r.change24 >= 0 ? "is-up" : "is-down"),
            render: (r) => fmtPct(r.change24),
          },
          {
            key: "volume",
            header: "Volume 24h",
            render: (r) => fmtCompact(r.volume),
          },
        ]}
      />
      <p className="sticky-markets-updated">Last updated: {lastUpdated(dex.updatedAt)}</p>

      {/* ---------- FOMO Trader Leaderboard ---------- */}
      <FomoLeaderboard />

      {/* ---------- STICKY — Not on the list...Yet card ---------- */}
      <div className="sticky-markets-teaser" data-testid="markets-sticky-teaser">
        <div className="sticky-markets-teaser-visual">
          <div className="sticky-markets-teaser-glow" aria-hidden="true" />
          <img src={ASSETS.coinGold} alt="Sticky Coin" className="sticky-markets-teaser-coin" />
        </div>
        <div className="sticky-markets-teaser-body">
          <div className="sticky-markets-teaser-badge">Not Listed · Yet</div>
          <h3 className="sticky-markets-teaser-title">
            <span className="sticky-accent">STICKY</span> — Not on the list…
            <span className="sticky-markets-teaser-yet"> Yet!</span>
          </h3>
          <p className="sticky-markets-teaser-text">
            <span className="sticky-markets-teaser-line">We're building.</span>
            <span className="sticky-markets-teaser-line">We're sticking around.</span>
            <span className="sticky-markets-teaser-line">Keep watching.</span>
          </p>
        </div>
      </div>
    </Section>
  );
}





function HomePage() {
  return (
    <div className="sticky-app" data-testid="sticky-app">
      <Header />
      {/* Glow background */}
      <div className="sticky-bg-glow" />
      {/* Hero particle layer */}
      <div className="sticky-particles" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} className={`sticky-particle sticky-particle-${i % 7}`} />
        ))}
      </div>
      {/* Decorative floating coins (desktop only) */}
      <img src={ASSETS.coin} alt="" className="sticky-decor-coin sticky-decor-coin-1" aria-hidden="true" />
      <img src={ASSETS.coin} alt="" className="sticky-decor-coin sticky-decor-coin-2" aria-hidden="true" />
      <FloatingBuy />

      <div className="sticky-container">
        {/* ============== HERO ============== */}
        <section className="sticky-hero sticky-reveal" data-testid="section-hero">
          <div className="sticky-fees-badge" data-testid="creator-fees-badge">
            <span className="sticky-fees-dot" />
            <span className="sticky-fees-text">
              Creator Fees <strong>→ 100%</strong> to Holders
            </span>
          </div>
          <div className="sticky-hero-mascot-wrap" data-testid="hero-mascot-wrap">
            <div className="sticky-hero-glow-ring" />
            <img
              src={ASSETS.mascot}
              alt="Sticky mascot holding STICKY coin"
              className="sticky-hero-mascot"
              data-testid="hero-mascot"
            />
          </div>
          <h1 className="sticky-h1" data-testid="hero-title">STICKY</h1>

          <a
            href={LINKS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="sticky-yt-pill"
            data-testid="hero-youtube-pill"
          >
            <span className="sticky-yt-icon" aria-hidden="true">
              <Youtube size={16} fill="#FFFFFF" stroke="#FFFFFF" strokeWidth={0} />
            </span>
            <span className="sticky-yt-text">
              Powered by <strong>StickyLikeYou</strong>
            </span>
          </a>

          <p className="sticky-hero-desc" data-testid="hero-desc">
            Sticky is more than just a meme coin. It’s a community-driven
            movement powered by content, creativity and real engagement.
          </p>

          <div className="sticky-hero-ca" data-testid="hero-contract-wrap">
            <ContractRow />
          </div>

          <div className="sticky-btn-group">
            <Button variant="primary" icon={Zap} href={LINKS.buy} testId="btn-buy">
              Buy Sticky
            </Button>
            <Button variant="secondary" icon={LineChart} href={DEXSCREENER.pairAddress ? `https://dexscreener.com/${DEXSCREENER.chain}/${DEXSCREENER.pairAddress}` : "#chart"} testId="btn-chart">
              View Live Chart
            </Button>
          </div>

          <div className="sticky-hero-socials" data-testid="hero-socials">
            <a
              href={LINKS.telegram || "#"}
              target={LINKS.telegram ? "_blank" : undefined}
              rel={LINKS.telegram ? "noopener noreferrer" : undefined}
              onClick={(e) => { if (!LINKS.telegram) e.preventDefault(); }}
              className={`sticky-hero-social ${!LINKS.telegram ? "is-soon" : ""}`}
              aria-label={LINKS.telegram ? "Telegram" : "Telegram — Coming Soon"}
              data-testid="hero-social-telegram"
            >
              <Send size={16} />
              {!LINKS.telegram && <span className="sticky-hero-social-badge">SOON</span>}
            </a>
            <a href={LINKS.twitter} target="_blank" rel="noopener noreferrer" className="sticky-hero-social" aria-label="X (Twitter)" data-testid="hero-social-twitter">
              <Twitter size={16} />
            </a>
            <a href={LINKS.youtube} target="_blank" rel="noopener noreferrer" className="sticky-hero-social" aria-label="YouTube" data-testid="hero-social-youtube">
              <Youtube size={16} />
            </a>
            <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" className="sticky-hero-social" aria-label="Instagram" data-testid="hero-social-instagram">
              <Instagram size={16} />
            </a>
          </div>
        </section>

        {/* ============== TRUST BAR ============== */}
        <section className="sticky-reveal sticky-trustbar" data-testid="trust-bar">
          {[
            { i: BadgeCheck, t: "Fair Launch" },
            { i: Lock, t: "No Presale" },
            { i: Users, t: "Community Driven" },
            { i: Eye, t: "Transparent Team & Dev" },
          ].map(({ i: Ic, t }) => (
            <div key={t} className="sticky-trust-pill" data-testid={`trust-${t}`}>
              <Ic size={13} strokeWidth={2.6} />
              <span>{t}</span>
            </div>
          ))}
        </section>

        {/* ============== RUGCHECK BUTTON ============== */}
        <RugcheckButton />

        {/* ============== TRANSPARENCY (Team Wallet + Launchpad Treasuries) ============== */}
        <TransparencySection teamWalletAddress={TEAM_WALLET} teamWalletActive={Boolean(TEAM_WALLET)} />

        {/* ============== PRICE TICKER ============== */}
        <PriceTicker />

        {/* ============== MARQUEE PHRASES ============== */}
        <div className="sticky-marquee" data-testid="marquee-ticker" aria-hidden="true">
          <div className="sticky-marquee-track">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              [
                "Community Powered",
                "Fair Launch",
                "Built Around StickyLikeYou",
                "Rewards",
                "Buybacks & Burn",
                "UNICEF Support",
                "Meme Ecosystem",
                "Transparent Development",
              ].map((phrase, i) => (
                <span key={`${k}-${i}`} className="sticky-marquee-item">
                  <span className="sticky-marquee-dot" />
                  {phrase}
                </span>
              ))
            )}
          </div>
        </div>

        {/* ============== LIVE CHART ============== */}
        <Section id="chart" eyebrow="Live Chart" title="Real-time $STICKY action" accentWord="$STICKY">
          <LiveChart />
        </Section>

        {/* ============== FOMO Top 5 Preview ============== */}
        <Section id="fomo-preview" eyebrow="Leaderboard" title="FOMO Traders" accentWord="FOMO">
          <FomoTop5Preview />
        </Section>

        {/* ============== LAUNCHPAD TEASER ============== */}
        <Section id="launchpad-teaser" eyebrow="Launchpad" title="STICKY Launchpad" accentWord="Launchpad">
          <div className="sticky-launchpad-teaser" data-testid="launchpad-teaser">
            <span className="sticky-lp-soon">
              <span className="sticky-lp-soon-dot" />
              Coming Soon
            </span>
            <h3 className="sticky-launchpad-teaser-title">
              STICKY <span className="sticky-accent">Launchpad</span>
            </h3>
            <p className="sticky-launchpad-teaser-tagline">Launch your token. Build your community.</p>
            <p className="sticky-launchpad-teaser-lede">
              A simple way to launch Solana tokens through the STICKY Ecosystem.
            </p>
            <div className="sticky-launchpad-teaser-cta">
              <Link to="/launchpad/create" className="sticky-lp-btn is-primary" data-testid="launchpad-teaser-create">
                <Rocket size={14} strokeWidth={2.4} />
                Create Token
              </Link>
              <Link to="/launchpad/explore" className="sticky-lp-btn is-ghost" data-testid="launchpad-teaser-explore">
                <Compass size={14} strokeWidth={2.4} />
                Explore Launches
              </Link>
            </div>
          </div>
        </Section>

        {/* ============== ABOUT ============== */}
        <Section id="about" eyebrow="About" title="Not just a coin" accentWord="coin">
          <p className="sticky-body">
            <strong>Sticky is more than just a meme coin.</strong>
          </p>
          <p className="sticky-body" style={{ marginTop: 12 }}>
            It’s a community-driven project built around creativity, content,
            and real engagement.
          </p>
          <p className="sticky-body" style={{ marginTop: 12 }}>
            Born from the YouTube channel <strong>StickyLikeYou</strong>, this
            token represents a new way of connecting people through
            entertainment and Web3.
          </p>
          <p className="sticky-body" style={{ marginTop: 12 }}>
            No empty promises. No hidden agendas. Just transparency,
            consistency, and a growing community.
          </p>
        </Section>

        {/* ============== WHY STICKY ============== */}
        <Section id="why" eyebrow="Why Sticky?" title="Built different" accentWord="different">
          <div className="sticky-stack sticky-why-stack">
            {[
              { i: Users, t: "Community First", d: "Holders shape every decision. The community is the project.", hasYT: false },
              { i: Youtube, t: "Content Powered", d: "Backed by StickyLikeYou — a real audience, not bots.", hasYT: true },
              { i: Eye, t: "Transparent Vision", d: "On-chain proofs, public roadmap, zero hidden agenda.", hasYT: false },
              { i: HandHeart, t: "Real Impact", d: "A share of activity funds verified charity (UNICEF CryptoFund).", hasYT: false },
              { i: Rocket, t: "Long-Term Ecosystem", d: "App, NFTs, partnerships — built phase by phase.", hasYT: false },
            ].map(({ i: Ic, t, d, hasYT }) => (
              <div className="sticky-card sticky-card-compact" key={t} data-testid={`why-${t}`}>
                <div className="sticky-card-icon sticky-card-icon-sm"><Ic size={16} /></div>
                <div className="sticky-card-compact-body">
                  <p className="sticky-card-title sticky-card-title-sm">{t}</p>
                  <p className="sticky-card-text sticky-card-text-sm">{d}</p>
                  {hasYT && (
                    <a
                      href={LINKS.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sticky-yt-mini-btn"
                      data-testid="why-youtube-btn"
                    >
                      <span className="sticky-yt-mini-icon"><Youtube size={12} fill="#FFFFFF" stroke="#FFFFFF" strokeWidth={0} /></span>
                      <span>Visit StickyLikeYou</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ============== TOKENOMICS ============== */}
        <Section id="tokenomics" eyebrow="Tokenomics" title="The numbers behind STICKY" accentWord="STICKY">
          <div className="sticky-stack sticky-why-stack" data-testid="tokenomics-grid">
            {[
              { i: Coins, t: "Supply", v: "1,000,000,000", d: "Total fixed supply of STICKY tokens." },
              { i: PieChart, t: "Allocation", v: "3% Team", d: "3% allocated to StickyLikeYou for content & development. The rest is for the open market." },
              { i: HandHeart, t: "Creator Fees", v: "100% to Holders", d: "Every single creator fee earned by the project is redistributed back to STICKY holders — 0% to the team.", highlight: true },
              { i: Flame, t: "Buybacks & Burn", v: "Deflationary", d: "Buybacks performed and tokens burned to reduce circulating supply over time." },
              { i: Droplets, t: "Liquidity", v: "Pump.fun → Raydium", d: "Fair launch on Pump.fun, then migration to Raydium DEX." },
              { i: Rocket, t: "Sticky Launchpad", v: "$99 · SOL / USDC", d: "The Sticky Launchpad charges a $99 launch fee, payable in SOL or USDC. 40% of every Launchpad fee is allocated to STICKY Buyback & Burn, creating a direct mechanism through which Launchpad activity contributes to the STICKY ecosystem. The remaining 60% supports Launchpad operations, development and ecosystem infrastructure." },
            ].map(({ i: Ic, t, v, d, highlight }) => (
              <div className={`sticky-card sticky-card-compact ${highlight ? "sticky-card-highlight" : ""}`} key={t}>
                <div className="sticky-card-icon sticky-card-icon-sm"><Ic size={16} /></div>
                <div className="sticky-card-compact-body">
                  <p className="sticky-card-title sticky-card-title-sm">{t}</p>
                  <p className="sticky-card-value sticky-card-value-sm">{v}</p>
                  <p className="sticky-card-text sticky-card-text-sm">{d}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="sticky-body" style={{ marginTop: 20 }}>
            Liquidity is created via Pump.fun and will migrate to Raydium (DEX).
            Buybacks will be performed and tokens will be burned to reduce supply.
          </p>
        </Section>

        {/* ============== HOW TO BUY ============== */}
        <Section id="howtobuy" eyebrow="Get Started" title="How to buy STICKY" accentWord="STICKY">
          <div className="sticky-stack">
            <div className="sticky-step sticky-step-vertical" data-testid="buy-step-1">
              <div className="sticky-step-num">1</div>
              <div className="sticky-step-content" style={{ flex: 1 }}>
                <h4>Download Phantom Wallet</h4>
                <p>Install Phantom Wallet to securely store and trade STICKY.</p>
                <a
                  href="https://phantom.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sticky-mini-btn"
                  data-testid="btn-phantom"
                >
                  <Download size={14} strokeWidth={2.6} />
                  <span>Download Phantom</span>
                </a>
              </div>
            </div>
            <div className="sticky-step sticky-step-vertical" data-testid="buy-step-2">
              <div className="sticky-step-num">2</div>
              <div className="sticky-step-content" style={{ flex: 1 }}>
                <h4>Buy SOL</h4>
                <p>Purchase SOL through Phantom or any supported exchange.</p>
              </div>
            </div>
            <div className="sticky-step sticky-step-vertical" data-testid="buy-step-3">
              <div className="sticky-step-num">3</div>
              <div className="sticky-step-content" style={{ flex: 1 }}>
                <h4>Go to Pump.fun / Raydium</h4>
                <p>Connect your wallet and access the STICKY token page.</p>
              </div>
            </div>
            <div className="sticky-step sticky-step-vertical" data-testid="buy-step-4">
              <div className="sticky-step-num">4</div>
              <div className="sticky-step-content" style={{ flex: 1 }}>
                <h4>Swap SOL for STICKY</h4>
                <p>Swap your SOL for STICKY and join the community.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* ============== HOW IT WORKS ============== */}
        <Section id="how" eyebrow="How it works" title="Simple flow, real impact" accentWord="real">
          <div className="sticky-stack">
            {[
              { n: 1, t: "Buy", d: "Get STICKY on Pump.fun (and later Raydium)." },
              { n: 2, t: "Engage", d: "Join the community, create memes, share content." },
              { n: 3, t: "Buyback", d: "Project performs buybacks fueled by activity." },
              { n: 4, t: "Burn", d: "Tokens are burned, reducing supply over time." },
              { n: 5, t: "Grow", d: "Ecosystem expands — App, NFTs, partnerships." },
            ].map((s) => (
              <div className="sticky-step" key={s.n} data-testid={`step-${s.n}`}>
                <div className="sticky-step-num">{s.n}</div>
                <div className="sticky-step-content">
                  <h4>{s.t}</h4>
                  <p>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ============== LIQUIDITY ============== */}
        <Section id="liquidity" eyebrow="Liquidity" title="From Pump.fun to Raydium">
          <div className="sticky-callout" data-testid="liquidity-callout">
            <Droplets size={28} color="#FFD84D" style={{ marginBottom: 10 }} />
            <p className="sticky-body" style={{ color: "#FFFFFF" }}>
              Liquidity is generated via <strong style={{ color: "#FFD84D" }}>Pump.fun</strong> and
              transitions to <strong style={{ color: "#FFD84D" }}>Raydium</strong> for open trading.
            </p>
          </div>
        </Section>

        {/* ============== COMMUNITY ============== */}
        <Section id="community" eyebrow="Community" title="Built around you" accentWord="you">
          <div className="sticky-stack sticky-why-stack">
            {[
              { i: Gift, t: "Airdrops", d: "Surprise drops for active holders and early supporters." },
              { i: Sparkles, t: "Giveaways", d: "Regular giveaways across Gleam, X, Telegram and YouTube.", card: "giveaway" },
              { i: ImageIcon, t: "Meme Contests", d: "Weekly meme competitions — best memes earn STICKY rewards.", card: "meme" },
              { i: Trophy, t: "Rewards", d: "Long-term rewards tied to engagement, not noise." },
            ].map(({ i: Ic, t, d, card }) => {
              if (card === "meme")     return <MemeContestsCard key={t} Icon={Ic} title={t} desc={d} />;
              if (card === "giveaway") return <GiveawayCard    key={t} Icon={Ic} title={t} desc={d} />;
              return (
                <div className="sticky-card sticky-card-compact" key={t} data-testid={`community-${t}`}>
                  <div className="sticky-card-icon sticky-card-icon-sm"><Ic size={16} /></div>
                  <div className="sticky-card-compact-body">
                    <p className="sticky-card-title sticky-card-title-sm">{t}</p>
                    <p className="sticky-card-text sticky-card-text-sm">{d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ============== STICKY APP ============== */}
        <Section id="app" eyebrow="The App" title="Sticky App" accentWord="App">
          <AppCarousel />

          <div className="sticky-card" data-testid="sticky-app-card" style={{ marginTop: 20 }}>
            <div className="sticky-card-icon"><Zap size={20} /></div>
            <div className="sticky-stack" style={{ marginBottom: 14 }}>
              {[
                { t: "Radio", i: Radio },
                { t: "Crypto News", i: Newspaper },
                { t: "Competitions", i: Trophy },
                { t: "Rewards", i: Gift },
                { t: "Live Leaderboard", i: TrendingUp },
                { t: "Giveaways", i: Sparkles },
                { t: "Community Features", i: Users },
                { t: "Sticky AI", i: Bot, soon: true },
                { t: "NFT Hub", i: ImageIcon, soon: true },
              ].map(({ t, i: Ic, soon }) => (
                <div key={t} className="sticky-app-feature">
                  <span className="sticky-app-feature-icon"><Ic size={14} /></span>
                  <span className="sticky-app-feature-text">{t}</span>
                  {soon && <span className="sticky-app-feature-soon">SOON</span>}
                </div>
              ))}
            </div>
            <p className="sticky-card-text">
              The Sticky App transforms the community into an interactive
              ecosystem powered by content, rewards, competitions, and
              real-world impact.
            </p>
          </div>
        </Section>

        {/* ============== MARKETS ============== */}
        <MarketsSection />

        {/* ============== NFT COLLECTION ============== */}
        <Section id="nft" eyebrow="NFTs" title="Sticky NFT Collection" accentWord="Sticky">
          <span className="sticky-soon">Coming Soon</span>
          <p className="sticky-body" style={{ marginBottom: 8 }}>
            A collection of <strong>10,000 unique Sticky NFTs</strong> with utility built in.
          </p>
          <div className="sticky-nft-grid" data-testid="nft-grid">
            {[
              { variant: "holo", label: "HOLO" },
              { variant: "gold", label: "GOLD" },
              { variant: "rare", label: "RARE" },
              { variant: "" }, { variant: "" }, { variant: "" },
              { variant: "" }, { variant: "" }, { variant: "" },
            ].map((tile, i) => (
              <div
                className={`sticky-nft-tile ${tile.variant ? `sticky-nft-${tile.variant}` : ""}`}
                key={i}
                data-testid={`nft-tile-${i + 1}`}
              >
                {tile.variant && (
                  <span className={`sticky-nft-badge sticky-nft-badge-${tile.variant}`}>
                    {tile.label}
                  </span>
                )}
                <img
                  src={ASSETS.coinGold}
                  alt={`Sticky NFT preview ${i + 1}`}
                  className="sticky-nft-img"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div className="sticky-stack" style={{ marginTop: 8 }}>
            {[
              "Unique traits",
              "Holder rewards",
              "Access to features",
              "Access to minigames",
              "Unlock badges",
              "Boost rewards",
            ].map((f) => (
              <div
                key={f}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  color: "#AAAAAA",
                  fontFamily: "Inter",
                  fontSize: 14,
                }}
              >
                <Check size={14} color="#FFD84D" />
                {f}
              </div>
            ))}
          </div>
        </Section>

        {/* ============== ROADMAP ============== */}
        <Section id="roadmap" eyebrow="Roadmap" title="The journey ahead" accentWord="journey">
          <RoadmapAccordion phases={[
            {
              phase: "Phase 1",
              title: "Launch & First Impact",
              items: [
                "Launch on Pump.fun",
                "Build initial community",
                "First holders join",
                "First donation made (proof shared publicly)",
              ],
            },
            {
              phase: "Phase 2",
              title: "Growth & Engagement",
              items: [
                "Content expansion",
                "Community growth",
                "Meme ecosystem",
                "Ongoing small donations based on activity",
              ],
            },
            {
              phase: "Phase 3",
              title: "DEX Migration",
              items: [
                "Migration to Raydium",
                "Increased liquidity",
                "Wider accessibility",
                "Growing impact alongside project growth",
              ],
            },
            {
              phase: "Phase 4",
              title: "Sticky Launchpad",
              items: [
                "Launch and expand the Sticky Launchpad on Solana",
                "Enable projects to create and deploy tokens through the Sticky ecosystem",
                "$99 launch fee payable in SOL or USDC",
                "40% of Launchpad fees allocated to STICKY Buyback & Burn",
              ],
            },
            {
              phase: "Phase 5",
              title: "Buybacks & Burn",
              items: [
                "Continuous buybacks",
                "Token burns",
                "Supply reduction",
                "Part of activity supports real-world causes",
              ],
            },
            {
              phase: "Phase 6",
              title: "Sticky App Launch",
              items: [
                "App release",
                "Competitions & rewards",
                "Community participation",
                "Donation integration through community milestones",
              ],
            },
            {
              phase: "Phase 7",
              title: "Expansion & Ecosystem",
              items: [
                "NFT collection integration",
                "Partnerships",
                "Global growth",
                "Larger-scale donations based on project success",
              ],
            },
          ]} />
        </Section>

        {/* ============== TRANSPARENCY & IMPACT ============== */}
        <Section id="impact" eyebrow="Transparency & Impact" title="Real growth, real impact" accentWord="impact">
          <div className="sticky-card" data-testid="giving-card">
            <div className="sticky-card-icon" style={{ background: "rgba(255,216,77,0.12)" }}>
              <HandHeart size={20} />
            </div>
            <p className="sticky-body" style={{ color: "#FFFFFF", marginBottom: 12 }}>
              <strong>Sticky is not just about growth — it’s about impact.</strong>
            </p>
            <p className="sticky-body" style={{ marginBottom: 16 }}>
              A share of project activity is directed toward supporting
              children in need, transparently and through trusted organizations
              like <strong style={{ color: "#FFD84D" }}>UNICEF CryptoFund</strong>.
            </p>

            <div className="sticky-impact-tracker" data-testid="impact-tracker">
              <div className="sticky-impact-row">
                <span className="sticky-impact-label">Donations to date</span>
                <span className="sticky-impact-value">$0 / —</span>
              </div>
              <div className="sticky-impact-bar">
                <span style={{ width: "0%" }} />
              </div>
              <p className="sticky-impact-note">
                <Sparkles size={12} /> First donation coming after launch
              </p>
            </div>

            <div className="sticky-grid-2" style={{ marginTop: 18 }}>
              <div className="sticky-impact-card">
                <BadgeCheck size={18} color="#FFD84D" />
                <p>On-chain verifiable</p>
              </div>
              <div className="sticky-impact-card">
                <Eye size={18} color="#FFD84D" />
                <p>Public proof shared</p>
              </div>
              <div className="sticky-impact-card">
                <TrendingUp size={18} color="#FFD84D" />
                <p>Scales with growth</p>
              </div>
              <div className="sticky-impact-card">
                <Shield size={18} color="#FFD84D" />
                <p>Trusted partners</p>
              </div>
            </div>

            <a
              href={LINKS.unicef}
              target="_blank"
              rel="noopener noreferrer"
              className="sticky-btn sticky-btn-primary"
              data-testid="btn-unicef"
              style={{ marginTop: 18 }}
            >
              <Heart size={18} strokeWidth={2.4} />
              <span>Donate via UNICEF CryptoFund</span>
            </a>
          </div>
        </Section>

        {/* ============== SOCIAL PROOF ============== */}
        <Section id="proof" eyebrow="Social Proof" title="Already a movement" accentWord="movement">
          <div className="sticky-social-proof" data-testid="social-proof">
            <img
              src={ASSETS.coinGold}
              alt="Sticky coin"
              className="sticky-social-coin"
              data-testid="social-coin"
            />
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <Youtube size={22} color="#FFD84D" />
              <p className="sticky-channel">StickyLikeYou</p>
            </div>
            <p className="sticky-subs">8K+</p>
            <p className="sticky-subs-label">Subscribers &amp; Growing</p>

            <div className="sticky-social-row">
              <a className="sticky-social-icon" href={LINKS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" aria-label="YouTube">
                <Youtube size={20} />
              </a>
              <a className="sticky-social-icon" href={LINKS.twitter} target="_blank" rel="noopener noreferrer" data-testid="social-twitter" aria-label="Twitter X">
                <Twitter size={20} />
              </a>
              <a
                className={`sticky-social-icon ${!LINKS.telegram ? "is-soon" : ""}`}
                href={LINKS.telegram || "#"}
                target={LINKS.telegram ? "_blank" : undefined}
                rel={LINKS.telegram ? "noopener noreferrer" : undefined}
                onClick={(e) => { if (!LINKS.telegram) e.preventDefault(); }}
                data-testid="social-telegram"
                aria-label={LINKS.telegram ? "Telegram" : "Telegram — Coming Soon"}
              >
                <Send size={20} />
                {!LINKS.telegram && <span className="sticky-hero-social-badge">SOON</span>}
              </a>
              <a className="sticky-social-icon" href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </Section>

        {/* ============== WE'RE HIRING ============== */}
        <Section id="hiring" eyebrow="Careers" title="We're Hiring 🚀" accentWord="Hiring">
          <HiringAccordion />
        </Section>

        {/* ============== TEAM ============== */}
        <Section id="team" eyebrow="The Sticky Team" title="Meet the builders" accentWord="builders">
          <div className="sticky-stack" data-testid="team-grid">
            {[
              {
                role: "Founder (Dev)",
                tag: "Lead",
                desc: "Builds & leads the STICKY ecosystem.",
              },
              {
                role: "Co-Founder",
                tag: "Community",
                desc: "Community & content direction.",
              },
              {
                role: "Builder",
                tag: "Engineering",
                desc: "Handles technical side & app development.",
              },
            ].map((m) => (
              <div className="sticky-team-card" key={m.role} data-testid={`team-${m.role}`}>
                <div className="sticky-team-avatar">
                  <img src={ASSETS.coinGold} alt="" />
                </div>
                <div className="sticky-team-info">
                  <span className="sticky-team-tag">{m.tag}</span>
                  <p className="sticky-team-role">{m.role}</p>
                  <p className="sticky-team-desc">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ============== FINAL BRAND MESSAGE ============== */}
        <Section id="final">
          <div className="sticky-final" data-testid="final-message">
            <h3>
              Sticky is more than a <span className="accent">token</span>.
            </h3>
            <p>It’s a community, an ecosystem, and a movement with real impact.</p>
          </div>
        </Section>

        {/* ============== FOOTER ============== */}
        <footer className="sticky-footer" data-testid="footer">
          <div className="sticky-social-row" style={{ marginBottom: 18 }}>
            <a className="sticky-social-icon" href={LINKS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <Youtube size={18} />
            </a>
            <a className="sticky-social-icon" href={LINKS.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter X">
              <Twitter size={18} />
            </a>
            <a
              className={`sticky-social-icon ${!LINKS.telegram ? "is-soon" : ""}`}
              href={LINKS.telegram || "#"}
              target={LINKS.telegram ? "_blank" : undefined}
              rel={LINKS.telegram ? "noopener noreferrer" : undefined}
              onClick={(e) => { if (!LINKS.telegram) e.preventDefault(); }}
              aria-label={LINKS.telegram ? "Telegram" : "Telegram — Coming Soon"}
            >
              <Send size={18} />
              {!LINKS.telegram && <span className="sticky-hero-social-badge">SOON</span>}
            </a>
            <a className="sticky-social-icon" href={LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={18} />
            </a>
          </div>

          <p>© {new Date().getFullYear()} STICKY · Powered by StickyLikeYou</p>
          <p className="disclaimer">
            This is a meme project. No financial guarantees. Always do your own
            research before participating in any crypto project.
          </p>
        </footer>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "auto" : "auto" });
  }, [pathname]);
  return null;
}

/* ======================================================================
   /tokenomics — dedicated public tokenomics page
   ====================================================================== */
function TokenomicsPage() {
  const caActive =
    typeof CONTRACT === "string" &&
    CONTRACT.length > 20 &&
    !/coming|launch|soon/i.test(CONTRACT);

  return (
    <div className="sticky-app">
      <div className="sticky-container sticky-tokenomics-page" data-testid="tokenomics-page">
        <Header />

        <Section
          id="official-tokenomics"
          eyebrow="Official Tokenomics"
          title="STICKY Token Supply & Allocation"
          accentWord="STICKY"
        >
          {/* ---- Supply overview (headline numbers) ---- */}
          <div className="sticky-tk-supply-grid" data-testid="tk-supply-grid">
            <div className="sticky-tk-supply-card is-primary" data-testid="tk-total-supply">
              <div className="sticky-tk-supply-icon"><Coins size={20} strokeWidth={2.4} /></div>
              <p className="sticky-tk-supply-label">Total Supply</p>
              <p className="sticky-tk-supply-value">1,000,000,000</p>
              <p className="sticky-tk-supply-ticker">STICKY</p>
              <p className="sticky-tk-supply-note">Fixed · No further minting</p>
            </div>
            <div className="sticky-tk-supply-card" data-testid="tk-circulating-supply">
              <div className="sticky-tk-supply-icon"><PieChart size={20} strokeWidth={2.4} /></div>
              <p className="sticky-tk-supply-label">Circulating Supply</p>
              <p className="sticky-tk-supply-value">1,000,000,000</p>
              <p className="sticky-tk-supply-ticker">STICKY</p>
              <p className="sticky-tk-supply-note">100% of supply in circulation</p>
            </div>
          </div>

          {/* ---- Contract Address ---- */}
          {caActive && (
            <div className="sticky-tk-ca" data-testid="tk-contract">
              <p className="sticky-tk-ca-label">Contract Address · Solana</p>
              <ContractRow />
              <div className="sticky-tk-ca-links">
                <a
                  href={`https://solscan.io/token/${CONTRACT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sticky-mini-btn"
                  data-testid="tk-solscan"
                >
                  <ExternalLink size={13} /> View on Solscan
                </a>
                <a
                  href={`https://birdeye.so/token/${CONTRACT}?chain=solana`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sticky-mini-btn"
                  data-testid="tk-birdeye"
                >
                  <ExternalLink size={13} /> Birdeye
                </a>
                <a
                  href={`https://dexscreener.com/solana/${CONTRACT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sticky-mini-btn"
                  data-testid="tk-dexscreener"
                >
                  <ExternalLink size={13} /> DexScreener
                </a>
              </div>
            </div>
          )}

          {/* ---- Detailed tokenomics (same cards as homepage, keeps design identical) ---- */}
          <h3 className="sticky-tk-subhead">Allocation & Mechanics</h3>
          <div className="sticky-stack sticky-why-stack" data-testid="tk-detail-grid">
            {[
              { i: Coins, t: "Supply", v: "1,000,000,000", d: "Total fixed supply of STICKY tokens — no further minting is possible." },
              { i: PieChart, t: "Allocation", v: "3% Team", d: "3% allocated to StickyLikeYou for content & development. The rest is for the open market." },
              { i: HandHeart, t: "Creator Fees", v: "100% to Holders", d: "Every single creator fee earned by the project is redistributed back to STICKY holders — 0% to the team.", highlight: true },
              { i: Flame, t: "Buybacks & Burn", v: "Deflationary", d: "Buybacks performed and tokens burned to reduce circulating supply over time." },
              { i: Droplets, t: "Liquidity", v: "Pump.fun → Raydium", d: "Fair launch on Pump.fun, with migration to Raydium DEX as the project matures." },
              { i: Rocket, t: "Sticky Launchpad", v: "$99 · SOL / USDC", d: "The Sticky Launchpad charges a $99 launch fee, payable in SOL or USDC. 40% of every Launchpad fee is allocated to STICKY Buyback & Burn, creating a direct mechanism through which Launchpad activity contributes to the STICKY ecosystem. The remaining 60% supports Launchpad operations, development and ecosystem infrastructure." },
            ].map(({ i: Ic, t, v, d, highlight }) => (
              <div className={`sticky-card sticky-card-compact ${highlight ? "sticky-card-highlight" : ""}`} key={t}>
                <div className="sticky-card-icon sticky-card-icon-sm"><Ic size={16} /></div>
                <div className="sticky-card-compact-body">
                  <p className="sticky-card-title sticky-card-title-sm">{t}</p>
                  <p className="sticky-card-value sticky-card-value-sm">{v}</p>
                  <p className="sticky-card-text sticky-card-text-sm">{d}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="sticky-body" style={{ marginTop: 24, textAlign: "center" }}>
            Liquidity is launched via Pump.fun and migrates to Raydium (DEX). Buybacks will be performed and tokens burned to reduce supply over time.
          </p>

          <div className="sticky-tk-cta-row">
            <a href={LINKS.buy} target="_blank" rel="noopener noreferrer" className="sticky-mini-btn is-primary" data-testid="tk-buy">
              <Zap size={14} strokeWidth={2.6} /> Buy STICKY
            </a>
            <a href={WHITEPAPER_URL} target="_blank" rel="noopener noreferrer" className="sticky-mini-btn" data-testid="tk-whitepaper">
              <Download size={13} /> Whitepaper
            </a>
            <Link to="/" className="sticky-mini-btn" data-testid="tk-home">
              Home
            </Link>
          </div>
        </Section>

        <footer className="sticky-footer" data-testid="tk-footer">
          <p>© {new Date().getFullYear()} STICKY · Powered by StickyLikeYou</p>
          <p className="disclaimer">
            This is a meme project. No financial guarantees. Always do your own research before participating in any crypto project.
          </p>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/tokenomics" element={<TokenomicsPage />} />
        <Route path="/launchpad" element={<LaunchpadHome />} />
        <Route path="/launchpad/create" element={<LaunchpadCreate />} />
        <Route path="/launchpad/explore" element={<LaunchpadExplore />} />
        <Route path="/launchpad/token/:slug" element={<LaunchpadTokenPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </>
  );
}

export default App;